import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Stack,
  Divider,
  Link,
  Chip,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import PublicIcon from '@mui/icons-material/Public';
import LocationOnIcon from '@mui/icons-material/LocationOn';

interface PersonContactSectionProps {
  person: {
    email?: string | null;
    phone?: string | null;
    nationality?: string | null;
    address?: string | null;
    aliases?: Array<{ alias: string }>;
    notes?: string | null;
  } | null;
}

const PersonContactSection: React.FC<PersonContactSectionProps> = ({ person }) => {
  if (!person) return null;

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PersonIcon color="primary" />
          Contact Information
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <Stack spacing={2}>
          {person.email ? (
            <Box display="flex" alignItems="center" gap={1}>
              <EmailIcon fontSize="small" color="action" />
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">
                  Email
                </Typography>
                <Link href={`mailto:${person.email}`} underline="hover">
                  {person.email}
                </Link>
              </Box>
            </Box>
          ) : (
            <Box display="flex" alignItems="center" gap={1}>
              <EmailIcon fontSize="small" color="disabled" />
              <Typography variant="body2" color="text.secondary">
                No email
              </Typography>
            </Box>
          )}

          {person.phone ? (
            <Box display="flex" alignItems="center" gap={1}>
              <PhoneIcon fontSize="small" color="action" />
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">
                  Phone
                </Typography>
                <Link href={`tel:${person.phone}`} underline="hover">
                  {person.phone}
                </Link>
              </Box>
            </Box>
          ) : (
            <Box display="flex" alignItems="center" gap={1}>
              <PhoneIcon fontSize="small" color="disabled" />
              <Typography variant="body2" color="text.secondary">
                No phone
              </Typography>
            </Box>
          )}

          {person.nationality ? (
            <Box display="flex" alignItems="center" gap={1}>
              <PublicIcon fontSize="small" color="action" />
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">
                  Nationality
                </Typography>
                <Typography variant="body2">{person.nationality}</Typography>
              </Box>
            </Box>
          ) : (
            <Box display="flex" alignItems="center" gap={1}>
              <PublicIcon fontSize="small" color="disabled" />
              <Typography variant="body2" color="text.secondary">
                No nationality
              </Typography>
            </Box>
          )}

          {person.address ? (
            <Box display="flex" alignItems="flex-start" gap={1}>
              <LocationOnIcon fontSize="small" color="action" sx={{ mt: 0.5 }} />
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">
                  Address
                </Typography>
                <Typography variant="body2">{person.address}</Typography>
              </Box>
            </Box>
          ) : (
            <Box display="flex" alignItems="center" gap={1}>
              <LocationOnIcon fontSize="small" color="disabled" />
              <Typography variant="body2" color="text.secondary">
                No address
              </Typography>
            </Box>
          )}

          {Array.isArray(person.aliases) && person.aliases.length > 0 && (
            <Box>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                Known Aliases
              </Typography>
              <Stack direction="row" spacing={0.5} flexWrap="wrap">
                {person.aliases.map((a: { alias: string }, idx: number) => (
                  <Chip key={idx} label={a.alias} size="small" variant="outlined" />
                ))}
              </Stack>
            </Box>
          )}

          {person.notes && (
            <Box>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                Notes
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {person.notes}
              </Typography>
            </Box>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};

export default PersonContactSection;
